/** The desktop Year's sheet — the pure half (W2).
 *
 *  No imports on purpose, so the Worker's suite can test it. Dates are ISO
 *  strings, the weekday rule is Monday-first as in `calendar.ts`, and a
 *  holiday set is any record keyed by ISO day (`countryHolidays(key, y)`).
 *
 *  The sheet is a column per day. Everything here answers one question about
 *  that grid: which column is a day, which columns does a bar cover, what
 *  reads up a day's column, what a drag would do.
 */

const D = (s: string) => new Date(s + 'T12:00:00Z')
const iso = (d: Date) => d.toISOString().slice(0, 10)
const addDays = (s: string, n: number) => iso(new Date(D(s).getTime() + n * 86400000))
const between = (a: string, b: string) => Math.round((D(b).getTime() - D(a).getTime()) / 86400000)
/** 0 Monday … 6 Sunday. */
const dow = (s: string) => (D(s).getUTCDay() + 6) % 7
// `hasOwn`, not `in` or a bare index: a holiday record is a plain object.
const isHoliday = (s: string, h: Record<string, unknown>) => Object.hasOwn(h, s)

export const yearStart = (year: number) => `${year}-01-01`
export const yearEnd = (year: number) => `${year}-12-31`
/** 365 or 366. */
export const yearLength = (year: number) => between(yearStart(year), yearEnd(year)) + 1

/** The column a day sits in, 0-based; negative before the year and past
 *  `yearLength - 1` after it. Not clamped — a caller clips on purpose. */
export const dayIndex = (year: number, day: string) => between(yearStart(year), day)

/** The day under a pointer: the track's left edge plus whole day widths. The
 *  element under the pointer is never asked, so a bar, a ghost or a label in
 *  the way cannot change the answer. Clamped to the year. */
export function dayAtX(year: number, clientX: number, trackLeft: number, dayWidth: number): string {
  const i = Math.floor((clientX - trackLeft) / dayWidth)
  const clamped = Math.max(0, Math.min(yearLength(year) - 1, i))
  return addDays(yearStart(year), clamped)
}

interface Spanned { starts_on: string; ends_on: string }

export interface Segment<T> {
  item: T
  /** Clipped to the year. */
  from: string
  to: string
  /** First column, and how many. */
  col: number
  span: number
  /** The item runs on past this edge of the year. */
  clipL: boolean
  clipR: boolean
}

/** Bars for one year: each item clipped to it, items wholly outside dropped.
 *  A stay from December to January is a bar in both years. */
export function segments<T extends Spanned>(items: T[], year: number): Segment<T>[] {
  const lo = yearStart(year)
  const hi = yearEnd(year)
  const out: Segment<T>[] = []
  for (const item of items) {
    if (item.ends_on < lo || item.starts_on > hi) continue
    const from = item.starts_on < lo ? lo : item.starts_on
    const to = item.ends_on > hi ? hi : item.ends_on
    out.push({
      item, from, to,
      col: dayIndex(year, from),
      span: between(from, to) + 1,
      clipL: item.starts_on < lo,
      clipR: item.ends_on > hi,
    })
  }
  return out
}

interface PlanLike { on_date: string; title: string; source: string; is_birthday: boolean }

export interface DayPlan {
  date: string
  text: string
  /** Every commitment that day came from Google Calendar, so the whole
   *  column reads in the quieter ink. */
  allGoogle: boolean
}

/** What reads up each day's column in Plans: the day's commitments joined with
 *  commas, Google-synced ones after the ones Doug made, birthdays left out.
 *  Stable within each group, so a day does not reshuffle on every refetch. */
export function plansByDay(commitments: PlanLike[], year: number): DayPlan[] {
  const by = new Map<string, PlanLike[]>()
  for (const c of commitments) {
    if (c.is_birthday || !c.on_date.startsWith(`${year}-`)) continue
    const list = by.get(c.on_date)
    if (list) list.push(c)
    else by.set(c.on_date, [c])
  }
  const out: DayPlan[] = []
  for (const [date, list] of by) {
    const mine = list.filter(c => c.source !== 'google' && c.source !== 'ics')
    const synced = list.filter(c => c.source === 'google' || c.source === 'ics')
    out.push({ date, text: [...mine, ...synced].map(c => c.title).join(', '), allGoogle: mine.length === 0 })
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : 1))
}

/** Leave a span of days would cost: weekdays that are not holidays of the region being looked at.
 *  The same rule `defaultLeaveFor` marks by, without needing a stay. */
export function leaveCost(from: string, to: string, holidays: Record<string, unknown>): number {
  let n = 0
  for (let d = from; d <= to; d = addDays(d, 1)) {
    if (dow(d) < 5 && !isHoliday(d, holidays)) n++
  }
  return n
}

export type DragMode = 'move' | 'resize-l' | 'resize-r'

/** Where a bar lands after a drag of `delta` whole days. A move keeps its
 *  length. An edge resize stops at the other edge — a one-day bar at the very
 *  least — so dragging the left edge past the right one pins it there instead
 *  of inverting the stay or being ignored. */
export function trimDrag(mode: DragMode, start: string, end: string, delta: number): Spanned {
  if (mode === 'move') return { starts_on: addDays(start, delta), ends_on: addDays(end, delta) }
  if (mode === 'resize-l') {
    const next = addDays(start, delta)
    return { starts_on: next > end ? end : next, ends_on: end }
  }
  const next = addDays(end, delta)
  return { starts_on: start, ends_on: next < start ? start : next }
}

/** The two ends of a create drag, in order. */
export function dragRange(anchor: string, cur: string): Spanned {
  return anchor <= cur ? { starts_on: anchor, ends_on: cur } : { starts_on: cur, ends_on: anchor }
}

/** What a travel stay's leave marks do when the stay goes from `from` to `to`.
 *
 *  A move carries the marks with it: each mark inside the old span shifts by
 *  the move, and one that would land on a weekend or a holiday is dropped
 *  rather than carried onto a day that cannot be leave. A resize keeps the
 *  marks still inside the new span, clears those outside, and marks the new
 *  weekdays it uncovers. Either way `clear` is the marks to remove and `set`
 *  the days to mark; a day in both lists is in neither.
 */
export function leaveDiff(
  from: Spanned, to: Spanned, marked: ReadonlySet<string>, holidays: Record<string, unknown>,
): { clear: string[]; set: string[] } {
  const oldLen = between(from.starts_on, from.ends_on)
  const newLen = between(to.starts_on, to.ends_on)
  const wanted = new Set<string>()
  const markable = (d: string) => dow(d) < 5 && !isHoliday(d, holidays)
  if (oldLen === newLen) {
    const delta = between(from.starts_on, to.starts_on)
    for (let d = from.starts_on; d <= from.ends_on; d = addDays(d, 1)) {
      if (!marked.has(d)) continue
      const n = addDays(d, delta)
      if (markable(n)) wanted.add(n)
    }
  } else {
    for (let d = to.starts_on; d <= to.ends_on; d = addDays(d, 1)) {
      const wasIn = d >= from.starts_on && d <= from.ends_on
      if (wasIn ? marked.has(d) : markable(d)) wanted.add(d)
    }
  }
  const clear: string[] = []
  for (let d = from.starts_on; d <= from.ends_on; d = addDays(d, 1)) {
    if (marked.has(d) && !wanted.has(d)) clear.push(d)
  }
  const set = [...wanted].filter(d => !marked.has(d)).sort()
  return { clear, set }
}

/** A leave set with a diff applied — the ruler shows this while a drag is in
 *  flight, before any write. */
export function applyLeave(marked: ReadonlySet<string>, diff: { clear: string[]; set: string[] }): Set<string> {
  const out = new Set(marked)
  for (const d of diff.clear) out.delete(d)
  for (const d of diff.set) out.add(d)
  return out
}

/** Days in a span, inclusive. */
export const spanDays = (from: string, to: string) => between(from, to) + 1

/** Public holidays inside a span, as `[day, name]` in date order. */
export function holidaysWithin(from: string, to: string, named: Record<string, string>): [string, string][] {
  const out: [string, string][] = []
  for (let d = from; d <= to; d = addDays(d, 1)) {
    if (isHoliday(d, named)) out.push([d, named[d]])
  }
  return out
}

export interface LeaveReading {
  /** Days past the allowance; 0 when within it. */
  over: number
  /** The counter's line: days free, or how far over. */
  text: string
  /** The bar, as percentages of its own width. The track grows to the total
   *  when marked days exceed the allowance, so the overrun is drawn rather
   *  than clipped; `mark` is where the allowance ends on it (null when not over). */
  spent: number
  held: number
  over_pct: number
  mark: number | null
}

/** What the leave counter and bar say. `free` is `allowance - spent - held`
 *  as `leaveStats` gives it, so a negative one is the overrun. One value used
 *  to be both "days left" and "how far over", and a minus sign told them
 *  apart; here they are two fields. */
export function leaveReading(spent: number, held: number, allowance: number): LeaveReading {
  const free = allowance - spent - held
  const over = Math.max(0, -free)
  const total = Math.max(allowance, spent + held)
  const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0)
  const inSpent = Math.min(spent, allowance)
  const inHeld = Math.min(held, Math.max(0, allowance - inSpent))
  return {
    over,
    text: over > 0 ? `${over} ${over === 1 ? 'day' : 'days'} over your ${allowance}` : `${free} of ${allowance} leave days free`,
    spent: pct(inSpent),
    held: pct(inHeld),
    over_pct: pct(over),
    mark: over > 0 ? pct(allowance) : null,
  }
}

/** How far to scroll the sheet this frame while a drag is held near its
 *  edge (a3-19, Doug's choice): `speed` px toward the edge within `zone` px
 *  of it, else 0. `left` is where the days start to show — the right edge of
 *  the sticky label column, not the scroller's own — and `right` the
 *  scroller's right edge. A pointer past an edge scrolls too: a drag that
 *  overshoots the sheet still means "further". */
export function edgeStep(x: number, left: number, right: number, zone = 40, speed = 12): number {
  if (right - left <= 2 * zone) return 0
  if (x < left + zone) return -speed
  if (x > right - zone) return speed
  return 0
}

/** Where "Add a trip" proposes a new one (a3-21): two weeks out in this
 *  year, mid-May in another, four days long; Doug edits it before Save. */
export function proposedTrip(year: number, today: string): Spanned {
  let start = today.startsWith(`${year}-`) ? addDays(today, 14) : `${year}-05-14`
  if (!start.startsWith(`${year}-`)) start = `${year}-05-14`
  return { starts_on: start, ends_on: addDays(start, 3) }
}
